import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { updateCard, importCardsTcgdex, fetchTcgdexSets } from "./cardsApi";
import CardsTcgdexSetsDialog from "./CardsTcgdexSetsDialog";
import CardsImportSummaryDialog from "./CardsImportSummaryDialog";
import CardsTcgdexImportDialog from "./CardsTcgdexImportDialog";

// TCGdex asset URLs may be provided without extension/quality (e.g. https://assets.tcgdex.net/.../001).
// Normalize to a direct image file URL for display.
const normalizeTcgdexImageUrl = (url) => {
  if (!url) return "";
  const s = String(url);
  const lower = s.toLowerCase();
  if (lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".webp")) {
    return s;
  }
  if (s.includes("assets.tcgdex.net/")) {
    return `${s}/high.png`;
  }
  return s;
};


function Cards() {
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isCopy, setIsCopy] = useState(false); // <-- NEW STATE
  const [quickSearch, setQuickSearch] = useState("");

  // Import-related state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);

  
  // TCGdex sets/series picker + sets viewer
  const [isSetsModalOpen, setIsSetsModalOpen] = useState(false);
  const [tcgdexSetsLoading, setTcgdexSetsLoading] = useState(false);
  const [tcgdexSetsRows, setTcgdexSetsRows] = useState([]);
  const [tcgdexSetsError, setTcgdexSetsError] = useState(null);

  const [tcgdexPickerSeriesId, setTcgdexPickerSeriesId] = useState("");
  const [tcgdexPickerSetId, setTcgdexPickerSetId] = useState("");

// TCGdex import
  const [isTcgdexModalOpen, setIsTcgdexModalOpen] = useState(false);
  const [tcgdexMode, setTcgdexMode] = useState("set_name"); // set_id | set_name | card_id
  const [tcgdexSetId, setTcgdexSetId] = useState("");
  const [tcgdexSetName, setTcgdexSetName] = useState("");
  const [tcgdexCardId, setTcgdexCardId] = useState("");
  const [tcgdexImporting, setTcgdexImporting] = useState(false);
  const [tcgdexResult, setTcgdexResult] = useState(null);
  const [tcgdexError, setTcgdexError] = useState(null);

  useEffect(() => {
    fetchCards();
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-injected', 'cards-set-name-red');
    style.textContent = `
    .MuiDataGrid-columnHeaders [data-field="set_name"] .MuiDataGrid-columnHeaderTitle {
      color: #d32f2f !important;
    }
  `;
    document.head.appendChild(style);
    return () => {
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    if (isSetsModalOpen) {
      ensureTcgdexSetsLoaded();
    }
  }, [isSetsModalOpen]);


  async function fetchCards() {
    try {
      const res = await axios.get("/api/cards");
      setRows(res.data.data || res.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  }

  const filteredRows = useMemo(() => {
    const term = String(quickSearch || "").trim().toLowerCase();
    if (!term) return rows;

    const searchableFields = [
      "name_and_number",
      "series_name",
      "series_code",
      "set_name",
      "type",
      "subtype",
      "rarity",
      "illustrator",
      "hp",
      "evolves_from",
    ];

    return (rows || []).filter((row) =>
      searchableFields.some((field) => {
        const value = row && row[field] != null ? String(row[field]).toLowerCase() : "";
        return value.includes(term);
      })
    );
  }, [rows, quickSearch]);

  async function ensureTcgdexSetsLoaded(force = false) {
    if (tcgdexSetsLoading) return;
    if (!force && tcgdexSetsRows && tcgdexSetsRows.length > 0) return;
    try {
      setTcgdexSetsError(null);
      setTcgdexSetsLoading(true);
      const res = await fetchTcgdexSets();
      const data = (res && res.data) ? res.data : res;
      const list = data && data.data ? data.data : (data || []);
      const normalized = (list || []).map((r, idx) => {
        const cc = r && r.card_count;
        const ccVal = (cc && typeof cc === 'object') ? (cc.total ?? cc.official ?? null) : cc;
        return {
          ...r,
          id: (r && (r.set_id || r.id)) ? (r.set_id || r.id) : idx,
          card_count: ccVal,
          total: (r && r.total != null) ? r.total : ((cc && typeof cc === 'object') ? (cc.total ?? null) : undefined),
          official: (r && r.official != null) ? r.official : ((cc && typeof cc === 'object') ? (cc.official ?? null) : undefined),
        };
      });
      setTcgdexSetsRows(normalized);
    } catch (e) {
      console.error("Failed to fetch TCGdex sets", e);
      setTcgdexSetsError(e && e.error ? e : (e && e.response && e.response.data ? e.response.data : { message: "Failed to fetch sets" }));
    } finally {
      setTcgdexSetsLoading(false);
    }
  }

  function getUniqueSeriesOptions() {
    const map = new Map();
    (tcgdexSetsRows || []).forEach((r) => {
      const sid = r.series_id || "";
      const sname = r.series_name || "";
      if (!sid && !sname) return;
      const key = sid || sname;
      if (!map.has(key)) {
        map.set(key, { series_id: sid || key, series_name: sname || String(key) });
      }
    });
    return Array.from(map.values()).sort((a, b) => String(a.series_name).localeCompare(String(b.series_name)));
  }

  function getFilteredSetOptions(seriesId) {
    const sid = seriesId || "";
    return (tcgdexSetsRows || [])
      .filter((r) => (sid ? String(r.series_id || r.series_name || "") === String(sid) : true))
      .map((r) => ({ set_id: r.set_id, set_name: r.set_name, series_name: r.series_name }))
      .filter((r) => r.set_id && r.set_name)
      .sort((a, b) => String(a.set_name).localeCompare(String(b.set_name)));
  }


  function openModifyModal(row) {
    setIsNew(false);
    setIsCopy(false); // <-- ensure not copy
    setSelectedCard(row);
    setFormValues({
      name_and_number: row.name_and_number || "",
      series_name: row.series_name || "",
      series_code: row.series_code || "",
      set_name: row.set_name || "",
      rarity: row.rarity || "",
      type: row.type || "",
      subtype: row.subtype || "",
      illustrator: row.illustrator || "",
      hp: row.hp ?? "",
      evolves_from: row.evolves_from || "",
      image_url: row.image_url || "",
      legal_in_standard:
        typeof row.legal_in_standard === "boolean"
          ? row.legal_in_standard
          : !!row.legal_in_standard,
      release_date: row.release_date
        ? String(row.release_date).split("T")[0]
        : "",
      tcgplayer_price: row.tcgplayer_price ?? "",
    });
    setIsModalOpen(true);
  }

  // NEW: openCopyModal – prefill from row but treat as a new card
  function openCopyModal(row) {
    setIsNew(true);
    setIsCopy(true); // <-- mark this as a copy action
    setSelectedCard(null);
    setFormValues({
      name_and_number: row.name_and_number || "",
      series_name: row.series_name || "",
      series_code: row.series_code || "",
      set_name: row.set_name || "",
      rarity: row.rarity || "",
      type: row.type || "",
      subtype: row.subtype || "",
      illustrator: row.illustrator || "",
      hp: row.hp ?? "",
      evolves_from: row.evolves_from || "",
      image_url: row.image_url || "",
      legal_in_standard:
        typeof row.legal_in_standard === "boolean"
          ? row.legal_in_standard
          : !!row.legal_in_standard,
      release_date: row.release_date
        ? String(row.release_date).split("T")[0]
        : "",
      tcgplayer_price: row.tcgplayer_price ?? "",
    });
    setIsModalOpen(true);
  }

  function openAddModal() {
    setIsNew(true);
    setIsCopy(false); // <-- ensure not copy
    setSelectedCard(null);
    setFormValues({
      name_and_number: "",
      series_name: "",
      series_code: "",
      set_name: "",
      rarity: "",
      type: "",
      subtype: "",
      illustrator: "",
      hp: "",
      evolves_from: "",
      image_url: "",
      legal_in_standard: false,
      release_date: "",
      tcgplayer_price: "",
    });
    setIsModalOpen(true);
  }

  function handleFieldChange(field) {
    return (e) => {
      let value;
      if (e.target.type === "checkbox") {
        value = e.target.checked;
      } else if (e.target.type === "number") {
        value = e.target.value === "" ? "" : Number(e.target.value);
      } else {
        value = e.target.value;
      }
      setFormValues((prev) => ({ ...prev, [field]: value }));
    };
  }

  async function handleSave() {
    // basic client-side validation
    if (
      !formValues.name_and_number ||
      String(formValues.name_and_number).trim() === ""
    ) {
      alert("Name and Number is required.");
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        // create
        const res = await axios.post("/api/cards", formValues);
        const created = res.data?.data || res.data || res;
        // if API returns the created record, append to rows
        if (created && created.id) {
          setRows((prev) => [created, ...prev]);
        } else {
          // If API returns different shape, try to refetch list or push the returned payload
          // Fallback: refetch entire list
          await fetchCards();
        }
      } else {
        // update existing
        if (!selectedCard) {
          console.warn("No selected card for update");
          setIsModalOpen(false);
          setSaving(false);
          return;
        }
        const updated = await updateCard(selectedCard.id, formValues, "PATCH");
        const updatedRow = updated.data || updated;
        setRows((prev) =>
          prev.map((r) => (r.id === updatedRow.id ? updatedRow : r))
        );
      }

      setIsModalOpen(false);
      setSelectedCard(null);
      setIsNew(false);
      setIsCopy(false); // <-- reset copy flag after successful save
    } catch (err) {
      console.error("Failed to save card", err);

      // When saving a copied card and an error occurs, show a human-readable message
      if (isCopy) {
        alert("Enter all applicable fields including date field to continue");
      } else if (err.response && err.response.status === 409) {
        // give user meaningful message when unique constraint (name_and_number) error occurs
        alert(
          "A card with that Name And Number already exists. Please change it."
        );
      } else if (err.response && err.response.data && err.response.data.message) {
        alert("Save failed: " + err.response.data.message);
      } else {
        alert("Failed to save card. See console for details.");
      }
    } finally {
      setSaving(false);
    }
  }

  // Import handlers
  function handleOpenImportModal() {
    setImportFile(null);
    setImportError(null);
    setIsImportModalOpen(true);
  }

  function handleFileChange(e) {
    setImportFile(
      e.target.files && e.target.files[0] ? e.target.files[0] : null
    );
  }

  async function handleRunImport() {
    if (!importFile) {
      alert("Please select a CSV file to import.");
      return;
    }

    const formData = new FormData();
    formData.append("file", importFile);

    setImporting(true);
    setImportError(null);
    try {
      const res = await axios.post("/api/cards/import_csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const summary = res.data;
      setImportResult(summary || null);
      setIsImportModalOpen(false);
      // refresh cards list after successful import
      await fetchCards();
    } catch (err) {
      console.error("Import failed", err);
      if (
        err.response &&
        err.response.status === 400 &&
        err.response.data &&
        err.response.data.message
      ) {
        // friendly validation error from server (e.g., missing required header)
        setImportError(err.response.data.message);
        alert(err.response.data.message);
      } else if (err.response && err.response.data && err.response.data.message) {
        setImportError(err.response.data.message);
        alert(err.response.data.message);
      } else {
        setImportError("Failed to import CSV. See console for details.");
        alert("Failed to import CSV. See console for details.");
      }
    } finally {
      setImporting(false);
    }
  }

  function handleDownloadTemplate() {
    axios
      .get("/api/cards/csv_template", { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "text/csv" })
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = "cards_template.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        alert("Failed to download template");
        console.error(err);
      });
  }


  function escapeCsvValue(value) {
    if (value == null) return "";
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  function handleDownloadCardsCsv() {
    try {
      const exportableColumns = columns.filter(
        (column) => !["actions", "scrape_metadata"].includes(column.field)
      );
      const headerRow = exportableColumns.map((column) => escapeCsvValue(column.headerName || column.field)).join(",");
      const dataRows = filteredRows.map((row) =>
        exportableColumns
          .map((column) => escapeCsvValue(row?.[column.field]))
          .join(",")
      );
      const csvContent = [headerRow, ...dataRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cards_ui_data.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download cards CSV", err);
      alert("Failed to download cards CSV.");
    }
  }

  function handleOpenTcgdexModal() {
    setTcgdexResult(null);
    setTcgdexError(null);
    setTcgdexImporting(false);
    // quick defaults for trial
    setTcgdexMode("set_name");
    setTcgdexSetId("me02.5");
    setTcgdexSetName("Ascended Heroes");
    setTcgdexCardId("");
    setIsTcgdexModalOpen(true);
    // preload sets for picker UX
    ensureTcgdexSetsLoaded();
  }

  async function handleRunTcgdexImport() {
    setTcgdexImporting(true);
    setTcgdexResult(null);
    setTcgdexError(null);

    try {
      let payload = {};
      if (tcgdexMode === "picker") {
        payload = { set_id: (tcgdexPickerSetId || "").trim() };
      } else if (tcgdexMode === "set_id") {
        payload = { set_id: (tcgdexSetId || "").trim() };
      } else if (tcgdexMode === "card_id") {
        payload = { card_id: (tcgdexCardId || "").trim() };
      } else {
        payload = { set_name: (tcgdexSetName || "").trim() };
      }

      const val = Object.values(payload)[0];
      if (!val) {
        setTcgdexError({ error: "Please enter a value to import." });
        return;
      }

      const res = await importCardsTcgdex(payload);
      setTcgdexResult(res);
      await fetchCards();
    } catch (err) {
      setTcgdexError(err);
    } finally {
      setTcgdexImporting(false);
    }
  }

  const handleScrapeMetadata = async (name_and_number) => {
    try {
      const res = await axios.post("/api/cards/scrape_metadata", {
        name_and_number,
      });
      alert(`Updated ${res.data.updated} card(s) with metadata.`);
      fetchCards();
    } catch (err) {
      alert("Failed to scrape metadata");
      console.error(err);
    }
  };

  const columns = [
    { field: "id", headerName: "Id", type: "number", width: 80, renderHeader: (params) => (
      <span style={{ color: '#d32f2f' }}>
        {params?.colDef?.headerName ?? params?.colDef?.field}
      </span>
    ) },
    {
      field: "name_and_number",
      headerName: "Name And Number",
      type: "string",
      width: 130,
      renderHeader: (params) => (
        <span style={{ color: 'brown' }}>
          {params?.colDef?.headerName ?? params?.colDef?.field}
        </span>
      )
    },
    { field: "set_name", headerName: "Set Name", type: "string", width: 130 },
    { field: "type", headerName: "Type", type: "string", width: 130 },
    { field: "subtype", headerName: "Subtype", type: "string", width: 130 },
    {
      field: "rarity",
      headerName: "Rarity",
      type: "string",
      width: 130,
      headerClassName: "rarity-header", // 👈 added class for blue header text
    },
    {
      field: "illustrator",
      headerName: "Illustrator",
      type: "string",
      width: 130,
    },
    { field: "hp", headerName: "Hp", type: "number", width: 130 },
    {
      field: "evolves_from",
      headerName: "Evolves From",
      type: "string",
      width: 130,
    },
    {
  field: "image_url",
  headerName: "Image Url",
  type: "string",
  width: 130,
  renderCell: (params) => {
    const raw = params.row?.image_url || "";
    const url = normalizeTcgdexImageUrl(raw);
    if (!url) return "";
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{ textDecoration: "underline" }}
        title={url}
      >
        Open
      </a>
    );
  },
},
    {
      field: "legal_in_standard",
      headerName: "Legal In Standard",
      type: "boolean",
      width: 130,
    },
    {
      field: "release_date",
      headerName: "Release Date",
      type: "string",
      width: 130,
    },
    {
      field: "tcgplayer_price",
      headerName: "Tcgplayer Price",
      type: "number",
      width: 130,
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 80,
      renderCell: (params) => {
        const raw = params.row?.created_at;
        if (!raw) return "";
        const date = new Date(raw);
        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleString("en-AU", { month: "short" });
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}-${month}-${year} ${hours}:${minutes}`;
      },
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      width: 80,
      renderCell: (params) => {
        const raw = params.row?.updated_at;
        if (!raw) return "";
        const date = new Date(raw);
        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleString("en-AU", { month: "short" });
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}-${month}-${year} ${hours}:${minutes}`;
      },
    },
    {
      field: "scrape_metadata",
      headerName: "Scrape Metadata",
      width: 160,
      renderCell: (params) =>
        (!params.row.rarity || !params.row.set_name) && (
          <Button
            variant="outlined"
            onClick={() => handleScrapeMetadata(params.row.name_and_number)}
          >
            Scrape
          </Button>
        ),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Button
            variant="contained"
            size="small"
            onClick={() => openModifyModal(params.row)}
            style={{ marginRight: 8 }}
          >
            Modify Card
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openCopyModal(params.row)}
          >
            Copy Card
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={async () => {
              if (!window.confirm("Are you sure you want to delete this card? This action cannot be undone.")) return;
              try {
                await axios.delete(`/api/cards/${params.row.id}`);
                setRows((prev) => prev.filter((r) => r.id !== params.row.id));
                alert("Card deleted successfully.");
              } catch (err) {
                console.error("Failed to delete card", err);
                if (err.response && err.response.data && err.response.data.message) {
                  alert("Delete failed: " + err.response.data.message);
                } else {
                  alert("Failed to delete card. See console for details.");
                }
              }
            }}
            style={{ marginLeft: 8 }}
          >
            Delete
          </Button>
        </>
      ),
    },
  

  ];
  return (
    <div style={{ height: "80vh", width: "100%" }}>
      <h2>Cards</h2>
      <div style={{ marginBottom: 16 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={openAddModal}
          style={{ marginRight: 8 }}
        >
          Add Card
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleOpenImportModal}
          style={{ marginRight: 8 }}
        >
          Import from CSV
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleOpenTcgdexModal}
          style={{ marginRight: 8 }}
        >
          Import from TCGdex
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setIsSetsModalOpen(true)}
          style={{ marginRight: 8 }}
        >
          View Sets
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleDownloadTemplate}
          style={{ marginRight: 8 }}
        >
          Download CSV Template
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleDownloadCardsCsv}
          style={{ marginRight: 8 }}
        >
          Download Cards to CSV
        </Button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <TextField
          label="Quick Search"
          size="small"
          value={quickSearch}
          onChange={(e) => setQuickSearch(e.target.value)}
          placeholder="Search name, set, series, rarity..."
          style={{ minWidth: 320 }}
        />
        <Button
          variant="text"
          onClick={() => setQuickSearch("")}
          disabled={!quickSearch}
        >
          Clear
        </Button>
        <div style={{ fontSize: 13, color: "#555" }}>
          Showing {filteredRows.length} of {rows.length} cards
        </div>
      </div>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.id}
        sx={{
          "& .rarity-header": {
            color: "blue", // 👈 makes the "Rarity" column header text blue
          },
        }}
      />

      {/* View Sets Modal */}
      <CardsTcgdexSetsDialog
        open={isSetsModalOpen}
        onClose={() => setIsSetsModalOpen(false)}
        rows={tcgdexSetsRows}
        loading={tcgdexSetsLoading}
        error={tcgdexSetsError}
        onDownloadSets={async () => {
          setTcgdexSetsRows([]);
          await ensureTcgdexSetsLoaded(true);
        }}
      />

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isNew ? "Add Card" : "Modify Card"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name And Number"
            fullWidth
            margin="dense"
            value={formValues.name_and_number || ""}
            onChange={handleFieldChange("name_and_number")}
          />
          <TextField
            label="Series Name"
            fullWidth
            margin="dense"
            value={formValues.series_name || ""}
            onChange={handleFieldChange("series_name")}
          />
          <TextField
            label="Series Code"
            fullWidth
            margin="dense"
            value={formValues.series_code || ""}
            onChange={handleFieldChange("series_code")}
          />
          <TextField
            label="Set Name"
            fullWidth
            margin="dense"
            value={formValues.set_name || ""}
            onChange={handleFieldChange("set_name")}
          />
          <TextField
            label="Rarity"
            fullWidth
            margin="dense"
            value={formValues.rarity || ""}
            onChange={handleFieldChange("rarity")}
          />
          <TextField
            label="Type"
            fullWidth
            margin="dense"
            value={formValues.type || ""}
            onChange={handleFieldChange("type")}
          />
          <TextField
            label="Subtype"
            fullWidth
            margin="dense"
            value={formValues.subtype || ""}
            onChange={handleFieldChange("subtype")}
          />
          <TextField
            label="Illustrator"
            fullWidth
            margin="dense"
            value={formValues.illustrator || ""}
            onChange={handleFieldChange("illustrator")}
          />
          <TextField
            label="Hp"
            type="number"
            fullWidth
            margin="dense"
            value={formValues.hp ?? ""}
            onChange={handleFieldChange("hp")}
          />
          <TextField
            label="Evolves From"
            fullWidth
            margin="dense"
            value={formValues.evolves_from || ""}
            onChange={handleFieldChange("evolves_from")}
          />
          <TextField
            label="Image Url"
            fullWidth
            margin="dense"
            value={formValues.image_url || ""}
            onChange={handleFieldChange("image_url")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formValues.legal_in_standard}
                onChange={handleFieldChange("legal_in_standard")}
                color="primary"
              />
            }
            label="Legal In Standard"
          />
          <TextField
            label="Release Date"
            type="date"
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={formValues.release_date || ""}
            onChange={handleFieldChange("release_date")}
          />
          <TextField
            label="Tcgplayer Price"
            type="number"
            step="0.01"
            fullWidth
            margin="dense"
            value={
              formValues.tcgplayer_price === "" ||
              formValues.tcgplayer_price === null
                ? ""
                : formValues.tcgplayer_price
            }
            onChange={handleFieldChange("tcgplayer_price")}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsModalOpen(false);
              setIsNew(false);
              setIsCopy(false); // <-- reset on cancel
              setSelectedCard(null);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {isNew ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TCGdex Import Modal */}
      <CardsTcgdexImportDialog
        open={isTcgdexModalOpen}
        onClose={() => setIsTcgdexModalOpen(false)}
        tcgdexImporting={tcgdexImporting}
        tcgdexMode={tcgdexMode}
        setTcgdexMode={setTcgdexMode}
        tcgdexPickerSeriesId={tcgdexPickerSeriesId}
        setTcgdexPickerSeriesId={setTcgdexPickerSeriesId}
        tcgdexPickerSetId={tcgdexPickerSetId}
        setTcgdexPickerSetId={setTcgdexPickerSetId}
        tcgdexSetsRows={tcgdexSetsRows}
        tcgdexSetsLoading={tcgdexSetsLoading}
        tcgdexSetsError={tcgdexSetsError}
        getUniqueSeriesOptions={getUniqueSeriesOptions}
        getFilteredSetOptions={getFilteredSetOptions}
        ensureTcgdexSetsLoaded={ensureTcgdexSetsLoaded}
        tcgdexSetId={tcgdexSetId}
        setTcgdexSetId={setTcgdexSetId}
        tcgdexSetName={tcgdexSetName}
        setTcgdexSetName={setTcgdexSetName}
        tcgdexCardId={tcgdexCardId}
        setTcgdexCardId={setTcgdexCardId}
        tcgdexError={tcgdexError}
        tcgdexResult={tcgdexResult}
        onRunImport={handleRunTcgdexImport}
      />

      {/* Import Modal */}
      <Dialog
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Cards from CSV</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 12 }}>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              style={{ display: "block" }}
            />
            <div style={{ marginTop: 8 }}>
              {importFile ? `Selected file: ${importFile.name}` : "No file selected"}
            </div>
            {importError ? (
              <div style={{ color: "red", marginTop: 8 }}>{importError}</div>
            ) : null}
          </div>
          <div style={{ fontSize: 13, color: "#666" }}>
            Select a CSV file with the required header "name_and_number". Unknown
            headers will be ignored and reported after import.
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsImportModalOpen(false);
              setImportFile(null);
              setImportError(null);
            }}
            disabled={importing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRunImport}
            variant="contained"
            disabled={importing}
          >
            {importing ? "Importing..." : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Summary Dialog */}
      <CardsImportSummaryDialog
        open={!!importResult}
        importResult={importResult}
        onClose={() => setImportResult(null)}
      />
    </div>
  );
}

export default Cards;
